'use client'

import React, { type ReactNode, useEffect, useState } from 'react'
import type { EmblaCarouselType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import { cn } from '@lib/utils'
import { Typography } from '@components/common/typography'
import { LottieSwipeHand } from './lottie-swipe-hand'

const PreivousArrow = () => (
  <CarouselButtonSvg className='aspect-[27/50] h-[30px] flex-shrink-0' />
)
const NextArrow = () => (
  <CarouselButtonSvg className='aspect-[27/50] h-[30px] flex-shrink-0 -scale-x-100' />
)

const CarouselButton = ({
  show = true,
  type,
  onClick,
  disabled = false,
  className,
}: {
  type: 'prev' | 'next'
  onClick: () => void
  show?: boolean
  disabled?: boolean
  className?: string
}) => {
  return (
    show && (
      <button
        disabled={disabled}
        aria-label={type === 'prev' ? 'previous item' : 'next item'}
        className={cn(
          'hidden',
          '[&:disabled]:cursor-not-allowed [&:disabled]:opacity-0',
          className,
          {
            flex: !disabled,
            embla__prev: type === 'prev',
            embla__next: type === 'next',
            'pr-4 xs:pr-6': type === 'prev',
            'pl-4 xs:pl-6': type === 'next',
          },
        )}
        onClick={onClick}
      >
        {type === 'next' ? <NextArrow /> : <PreivousArrow />}
      </button>
    )
  )
}

export const FourColumnCarousel = ({ slides }: { slides: ReactNode[] }) => {
  const [viewportRef, embla] = useEmblaCarousel({
    slidesToScroll: 'auto',
  })
  const [prevButtonDisabled, setPrevButtonDisabled] = useState(true)
  const [nextButtonDisabled, setNextButtonDisabled] = useState(true)
  const [hasScrolled, setHasScrolled] = useState(false)

  const scrollPrev = () => embla?.scrollPrev()
  const scrollNext = () => embla?.scrollNext()

  const toggleButtonsDisabled = (embla: EmblaCarouselType | undefined) => {
    setPrevButtonDisabled(!embla?.canScrollPrev())
    setNextButtonDisabled(!embla?.canScrollNext())
  }

  const onSelect = (embla: EmblaCarouselType | undefined) => {
    toggleButtonsDisabled(embla)
    if (!hasScrolled) {
      setHasScrolled(true)
    }
  }

  useEffect(() => {
    if (embla === undefined) {
      return
    }

    toggleButtonsDisabled(embla)
    embla.on('reInit', toggleButtonsDisabled)
    embla.on('select', onSelect)

    return () => {
      embla.off('reInit', toggleButtonsDisabled)
      embla.off('select', onSelect)
    }
  }, [embla])

  return (
    <div className='relative'>
      <div
        className={cn(
          'embla',
          'relative h-full',
          'max-lg:mx-[-5vw] max-lg:w-[100vw]',
        )}
      >
        <div
          className={cn(
            'embla__viewport',
            'w-full overflow-x-hidden',
            'max-lg:px-[5vw]',
            'max-lg:select-none',
            'max-lg:cursor-grab',
            'max-lg:[&:active]:cursor-grabbing',
          )}
          ref={viewportRef}
        >
          <div
            className={cn(
              'embla_container',
              'grid grid-rows-[repeat(2,min-content)] items-center gap-x-[10px] gap-y-0',
              // force carousel under LG, with set 240px fixed width items
              'max-lg:grid-cols-[repeat(4,min(240px,90vw))]',
              // 0px - should never get that small, but forces anything above LG not to need scroll
              'lg:grid-cols-[repeat(4,minmax(0px,min(calc((100%-30px)/4),90vw)))]',
            )}
          >
            {slides}
          </div>
        </div>
        <div
          className={cn(
            'absolute left-0 top-0 hidden h-full w-full items-center justify-center text-white',
            'pointer-events-none  bg-[rgba(0,0,0,0.7)] opacity-100 transition-opacity',
            {
              flex: !prevButtonDisabled || !nextButtonDisabled,
              'opacity-0': hasScrolled,
            },
          )}
        >
          <div className='flex flex-col gap-2'>
            <LottieSwipeHand className='aspect-1 h-32 w-32' />
            <Typography as='div' variant='body-small' className='font-bold'>
              SWIPE LEFT
            </Typography>
          </div>
        </div>
      </div>
      <div
        className={cn('mt-2 hidden items-center justify-between', {
          flex: !prevButtonDisabled || !nextButtonDisabled,
        })}
      >
        <CarouselButton
          disabled={prevButtonDisabled}
          type='prev'
          onClick={scrollPrev}
          className='relative'
        />
        <CarouselButton
          disabled={nextButtonDisabled}
          type='next'
          onClick={scrollNext}
          className='relative ml-auto'
        />
      </div>
    </div>
  )
}

const CarouselButtonSvg = ({ className }: { className?: string }) => (
  <svg
    aria-hidden={true}
    className={className}
    width='27'
    height='50'
    viewBox='0 0 27 50'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M26.0416 0.744141L2 24.7858L26.0416 48.8274'
      stroke='#F1325F'
      strokeWidth={4}
    />
  </svg>
)
